import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Pencil, Trash2, DollarSign, Eye, Search, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge, Textarea } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import { supplierService, supplierPaymentService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import { useCRUD } from '../../hooks/useCRUD';
import toast from 'react-hot-toast';

export default function SuppliersPage() {
  const crud = useCRUD(supplierService, 'Supplier');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [payTarget, setPayTarget]       = useState(null);
  const [payments, setPayments]         = useState([]);
  const [payLoading, setPayLoading]     = useState(false);
  const [savingPay, setSavingPay]       = useState(false);
  const [editPay, setEditPay]           = useState(null);
  const [editPayForm, setEditPayForm]   = useState({ amount:'', method:'cash', reference:'', notes:'' });
  const [savingEditPay, setSavingEditPay] = useState(false);
  const [delPay, setDelPay]             = useState(null);
  const [deletingPay, setDeletingPay]   = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const payForm = useForm();

  useEffect(() => { crud.fetchData(); }, [crud.page, crud.search]); // eslint-disable-line
  useEffect(() => {
    if (crud.editItem) reset({ ...crud.editItem });
    else reset({ name:'', company:'', phone:'', phone2:'', email:'', address:'', city:'', country:'', contactPerson:'', taxNumber:'', bankAccount:'', bankName:'', notes:'' });
  }, [crud.editItem]); // eslint-disable-line

  const openPay = async (supplier) => {
    setPayTarget(supplier);
    setPayLoading(true);
    try {
      const res = await supplierPaymentService.getPayments(supplier._id);
      setPayments(res.data.data || []);
    } catch {} finally { setPayLoading(false); }
  };

  const onPay = async (data) => {
    if (!payTarget) return;
    setSavingPay(true);
    try {
      await supplierPaymentService.addPayment(payTarget._id, { ...data, amount: Number(data.amount) });
      toast.success('Payment recorded');
      payForm.reset();
      await reloadPayments();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSavingPay(false); }
  };

  const reloadPayments = async () => {
    if (!payTarget) return;
    const res = await supplierPaymentService.getPayments(payTarget._id);
    setPayments(res.data.data || []);
    // به‌روزرسانی payTarget با مقدار تازه totalDebt (مودال از payTarget می‌خواند، نه از لیست)
    try {
      const supRes = await supplierService.getById(payTarget._id);
      if (supRes?.data?.data) setPayTarget(supRes.data.data);
    } catch {}
    crud.fetchData();
  };

  const openEditPay = (p) => {
    setEditPay(p);
    setEditPayForm({ amount: p.amount, method: p.method||'cash', reference: p.reference||'', notes: p.notes||'' });
  };

  const handleSaveEditPay = async () => {
    if (!editPayForm.amount || Number(editPayForm.amount) <= 0) return toast.error('Enter a valid amount');
    setSavingEditPay(true);
    try {
      await supplierPaymentService.updatePayment(payTarget._id, editPay._id, {
        amount: Number(editPayForm.amount), method: editPayForm.method,
        reference: editPayForm.reference, notes: editPayForm.notes,
      });
      toast.success('Payment updated');
      setEditPay(null);
      await reloadPayments();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSavingEditPay(false); }
  };

  const handleDeletePay = async () => {
    setDeletingPay(true);
    try {
      await supplierPaymentService.deletePayment(payTarget._id, delPay._id);
      toast.success('Payment deleted, supplier debt restored');
      setDelPay(null);
      await reloadPayments();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeletingPay(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await supplierService.delete(deleteTarget._id);
      toast.success('Supplier deleted/deactivated');
      crud.fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true,
      render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900">{v}</p>
          {row.company && <p className="text-xs text-gray-400">{row.company}</p>}
        </div>
      )
    },
    { key: 'phone',         label: 'Phone' },
    { key: 'contactPerson', label: 'Contact' },
    { key: 'city',          label: 'City' },
    { key: 'totalDebt', label: 'Debt',
      render: (v) => <span className={v > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-medium'}>{formatCurrency(v || 0)}</span>
    },
    { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
  ];

  const FormFields = ({ form: { register, formState: { errors } } }) => (
    <div className="grid grid-cols-2 gap-4">
      <FormField label="Supplier Name" required error={errors.name?.message}>
        <Input {...register('name', { required: 'Required' })} placeholder="Supplier name" />
      </FormField>
      <FormField label="Company">
        <Input {...register('company')} placeholder="Company name" />
      </FormField>
      <FormField label="Phone" required error={errors.phone?.message}>
        <Input {...register('phone', { required: 'Required' })} placeholder="+93 700 000 000" />
      </FormField>
      <FormField label="Phone 2">
        <Input {...register('phone2')} placeholder="Alternative phone" />
      </FormField>
      <FormField label="Email">
        <Input type="email" {...register('email')} placeholder="email@company.com" />
      </FormField>
      <FormField label="Contact Person">
        <Input {...register('contactPerson')} placeholder="Contact name" />
      </FormField>
      <FormField label="City">
        <Input {...register('city')} placeholder="City" />
      </FormField>
      <FormField label="Country">
        <Input {...register('country')} placeholder="Country" />
      </FormField>
      <FormField label="Tax Number">
        <Input {...register('taxNumber')} placeholder="Tax ID" />
      </FormField>
      <FormField label="Bank Name">
        <Input {...register('bankName')} placeholder="Bank name" />
      </FormField>
      <div className="col-span-2">
        <FormField label="Bank Account Number">
          <Input {...register('bankAccount')} placeholder="Account number" />
        </FormField>
      </div>
      <div className="col-span-2">
        <FormField label="Address">
          <textarea {...register('address')} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Full address" />
        </FormField>
      </div>
      <div className="col-span-2">
        <FormField label="Notes">
          <textarea {...register('notes')} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notes" />
        </FormField>
      </div>
    </div>
  );

  return (
    <div>
      <Breadcrumb />
      <PageHeader title="Suppliers" subtitle="Manage suppliers and payments"
        actions={<Button onClick={crud.openCreate}><Plus size={16} /> Add Supplier</Button>} />

      {/* Desktop */}
      <div className="hidden md:block">
        <DataTable columns={columns} data={crud.data} loading={crud.loading}
          totalItems={crud.total} page={crud.page} pageSize={20}
          onPageChange={crud.setPage} onSearch={crud.setSearch}
          title="Suppliers" exportFilename="suppliers"
          actions={(row) => (
            <>
              <button onClick={() => openPay(row)} className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Payments"><DollarSign size={14} /></button>
              <button onClick={() => crud.openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Edit"><Pencil size={14} /></button>
              <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
            </>
          )}
        />
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-3">
        {crud.data.map(row => (
          <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">{row.name}</p>
                {row.company && <p className="text-xs text-gray-400">{row.company}</p>}
                <p className="text-xs text-gray-500">{row.phone}</p>
              </div>
              <span className={`text-sm font-bold ${row.totalDebt > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(row.totalDebt || 0)}</span>
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-50">
              <button onClick={() => openPay(row)} className="flex-1 text-xs py-1.5 rounded bg-green-50 text-green-700 font-medium">Payments</button>
              <button onClick={() => crud.openEdit(row)} className="flex-1 text-xs py-1.5 rounded bg-blue-50 text-blue-700 font-medium">Edit</button>
              <button onClick={() => setDeleteTarget(row)} className="flex-1 text-xs py-1.5 rounded bg-red-50 text-red-700 font-medium">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={crud.modalOpen} onClose={crud.closeModal} title={crud.editItem ? 'Edit Supplier' : 'Add Supplier'} size="lg">
        <form onSubmit={handleSubmit(crud.handleSave)} className="space-y-4">
          <FormFields form={{ register, formState: { errors } }} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={crud.closeModal}>Cancel</Button>
            <Button type="submit" loading={crud.saving}>{crud.editItem ? 'Update' : 'Save'}</Button>
          </div>
        </form>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={!!payTarget} onClose={() => setPayTarget(null)} title={`Payments — ${payTarget?.name}`} size="lg">
        {payTarget && (
          <div className="space-y-4">
            {/* Debt summary */}
            <div className="flex items-center justify-between bg-red-50 rounded-lg p-4 border border-red-100">
              <div>
                <p className="text-xs text-red-500">Total Debt</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(payTarget.totalDebt || 0)}</p>
              </div>
              <DollarSign size={28} className="text-red-300" />
            </div>

            {/* Add payment form */}
            {(payTarget.totalDebt || 0) > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-sm font-semibold text-gray-700 mb-3">Record Payment</p>
                <form onSubmit={payForm.handleSubmit(onPay)} className="grid grid-cols-2 gap-3">
                  <FormField label="Amount" required>
                    <Input type="number" step="0.01" max={payTarget.totalDebt}
                      {...payForm.register('amount', { required: true, min: 0.01 })} placeholder="0.00" />
                  </FormField>
                  <FormField label="Method">
                    <Select {...payForm.register('method')}>
                      <option value="cash">Cash</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="card">Card</option>
                      <option value="mobile">Mobile</option>
                    </Select>
                  </FormField>
                  <FormField label="Reference No.">
                    <Input {...payForm.register('reference')} placeholder="Receipt / transfer ref." />
                  </FormField>
                  <FormField label="Notes">
                    <Input {...payForm.register('notes')} placeholder="Notes" />
                  </FormField>
                  <div className="col-span-2 flex justify-end">
                    <Button type="submit" loading={savingPay} className="bg-green-600 hover:bg-green-700 text-white">
                      <DollarSign size={15} /> Record Payment
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Payment history */}
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">Payment History</p>
              {payLoading ? <div className="h-16 bg-gray-100 rounded animate-pulse" /> :
                payments.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No payments recorded</p>
                ) : (
                  <div className="border border-gray-100 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50"><tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Amount</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Method</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Reference</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Date</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-gray-400">Actions</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-50">
                        {payments.map(p => (
                          <tr key={p._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-semibold text-green-700">{formatCurrency(p.amount)}</td>
                            <td className="px-3 py-2 capitalize text-gray-600">{p.method}</td>
                            <td className="px-3 py-2 text-gray-400 text-xs">{p.reference || '-'}</td>
                            <td className="px-3 py-2 text-gray-400 text-xs">{toJalali(p.createdAt)}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={()=>openEditPay(p)} className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Edit"><Pencil size={13}/></button>
                                <button onClick={()=>setDelPay(p)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={13}/></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Payment Modal */}
      <Modal isOpen={!!editPay} onClose={()=>setEditPay(null)} title="Edit Payment" size="sm">
        {editPay && (
          <div className="space-y-3">
            <FormField label="Amount" required>
              <Input type="number" step="0.01" min="0" value={editPayForm.amount}
                onChange={e=>setEditPayForm(p=>({...p, amount:e.target.value}))} placeholder="0.00"/>
            </FormField>
            <FormField label="Method">
              <Select value={editPayForm.method} onChange={e=>setEditPayForm(p=>({...p, method:e.target.value}))}>
                <option value="cash">Cash</option>
                <option value="bank">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="mobile">Mobile</option>
              </Select>
            </FormField>
            <FormField label="Reference No.">
              <Input value={editPayForm.reference} onChange={e=>setEditPayForm(p=>({...p, reference:e.target.value}))} placeholder="Receipt / transfer ref."/>
            </FormField>
            <FormField label="Notes">
              <Input value={editPayForm.notes} onChange={e=>setEditPayForm(p=>({...p, notes:e.target.value}))} placeholder="Notes"/>
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={()=>setEditPay(null)}>Cancel</Button>
              <Button onClick={handleSaveEditPay} loading={savingEditPay}>Save</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Payment Confirm */}
      <ConfirmModal isOpen={!!delPay} onClose={()=>setDelPay(null)} onConfirm={handleDeletePay} loading={deletingPay}
        title="Delete Payment" confirmLabel="Delete"
        message={`Delete payment of ${formatCurrency(delPay?.amount)}? Supplier debt will be restored.`}/>

      <ConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting}
        title="Delete Supplier" message={`Delete "${deleteTarget?.name}"? If they have purchases, they will be deactivated.`} />
    </div>
  );
}
